import { COOKIE_KEYS, type UserStoreCookie } from "~/constants/cookies";
import {
  determineDefaultSpace,
  getAvailableSpaces,
  updateSelectedSpace,
} from "~/utils/spaceManagement";
import { isCityPath, opensUserSpace, spacesOfPath } from "~/utils/spacesOfPath";
import Role, { type RoleWithPermissions } from "~/types/Role";

export default defineNuxtRouteMiddleware(async (to, _from) => {
  // Early return for guest routes - skip all middleware logic
  const isGuestRoute = to.meta.middleware?.includes("sanctum:guest");
  if (isGuestRoute) return;

  // Only run on client side
  if (import.meta.client === false) return;

  const userStoreCookie = useCookie<UserStoreCookie>(COOKIE_KEYS.USER_STORE);
  const userStore = useUserStore();
  const user = toValue(
    userStoreCookie?.value?.user ?? (await userStore.getUser()),
  );

  // ── Authentication check ──────────────────────────────────────
  if (!user) {
    return navigateTo("/login");
  }

  // ── Permissions loading (formerly permissions.ts) ─────────────
  // This guard runs on every navigation. Fetch once per user, otherwise each
  // denied route triggered another round-trip to /api/users/rolesAndPermissions.
  const permissionsLoadedForUserId = useState<number | null>(
    "access-guard:permissions-loaded-for",
    () => null,
  );
  if (permissionsLoadedForUserId.value !== user.id) {
    try {
      // Read the rights from this session's /api/user response, never from the
      // USER_STORE cookie. Older backends do not send the field: fall back to
      // the dedicated endpoint.
      const identity = useSanctumUser<{ id?: number; roles_and_permissions?: unknown }>().value;
      let rolesWithPermissions: unknown = identity?.id === user.id
        ? identity?.roles_and_permissions
        : undefined;
      if (Array.isArray(rolesWithPermissions) === false) {
        const apiFetch = useSanctumClient();
        rolesWithPermissions = await apiFetch(authUrl.rolesAndPermissions);
      }
      // A non-array response (a `{ data: [...] }` envelope, pagination) must not
      // be silently read as "no rights at all" and then cached. Throw and retry.
      if (Array.isArray(rolesWithPermissions) === false) {
        throw new Error("Réponse invalide du serveur : rolesAndPermissions");
      }
      const roleList = rolesWithPermissions as RoleWithPermissions[];
      const userPermissions = usePermissions();
      const userRoles = useRoles();
      const roles = new Set<string>();
      const permissions = new Set<string>();
      for (let i = 0; i < roleList.length; i++) {
        const roleWithPermissions = roleList[i];
        roles.add(roleWithPermissions.name);
        // `permissions` is optional in types/Role.ts: a role with no rights can
        // arrive without the key at all.
        const rolePermissions = roleWithPermissions.permissions ?? [];
        for (let j = 0; j < rolePermissions.length; j++) {
          permissions.add(rolePermissions[j].name);
        }
      }
      userPermissions.value = Array.from(permissions);
      userRoles.value = Array.from(roles);
      permissionsLoadedForUserId.value = user.id;
    }
    catch (e) {
      // Leave `permissionsLoadedForUserId` null so the next navigation retries.
      console.error(e);
    }
  }

  // ── Auditor-only redirect (formerly 1.role-guard.global.ts) ───
  // `[].every(...)` is `true`. Without the length test, a user with no role at
  // all was treated as an auditor and sent to /audit-passengers, the one page
  // that requires the Auditeur role. The denial then looped forever.
  const userIsOnlyAnAuditor
    = (user.roles?.length ?? 0) > 0
      && user.roles.every((role: string) => role === Role.AUDITOR);
  if (userIsOnlyAnAuditor && to.path !== "/audit-passengers") {
    return navigateTo("/audit-passengers");
  }

  // ── City / Space guard (formerly city-guard.global.ts) ────────
  const userRoleNames = user.roles ?? [];
  const hasCityRole = userRoleNames.includes(Role.CITY) ?? false;
  const hasSuperAdminRole = userRoleNames.includes(Role.SUPER_ADMIN) ?? false;
  const hasAuditorRole = userRoleNames.includes(Role.AUDITOR) ?? false;
  const availableSpaces = getAvailableSpaces(userRoleNames);

  // Sync cookie and store if they're out of sync
  let currentSelectedSpace = userStoreCookie.value?.selectedSpace || null;
  if (currentSelectedSpace !== userStore.selectedSpace) {
    userStore.setSelectedSpace(currentSelectedSpace);
    currentSelectedSpace = userStore.selectedSpace;
  }

  if (currentSelectedSpace && availableSpaces.length > 0 && !availableSpaces.includes(currentSelectedSpace)) {
    updateSelectedSpace(userStore, userStoreCookie, null);
    currentSelectedSpace = null;
  }

  // Auto-select default space if none is selected
  if (!currentSelectedSpace && userRoleNames.length) {
    const defaultSpace = determineDefaultSpace(hasCityRole, hasSuperAdminRole, hasAuditorRole, userRoleNames);
    if (defaultSpace) {
      updateSelectedSpace(userStore, userStoreCookie, defaultSpace);
      currentSelectedSpace = defaultSpace;
    }
  }

  // Handle users with multiple spaces who need to choose
  if (
    availableSpaces.length > 1
    && !currentSelectedSpace
    && to.path !== "/choice-space"
  ) {
    return navigateTo("/choice-space");
  }

  // Keep the choice page accessible for multi-space users so they can switch spaces.
  if (currentSelectedSpace && availableSpaces.length <= 1 && to.path === "/choice-space") {
    if (currentSelectedSpace === "auditor") return navigateTo("/audit-passengers");
    return navigateTo(currentSelectedSpace === "city" ? "/city" : "/");
  }

  // Enforce space-based routing
  const routeRoles = typeof to.meta.roles === "string"
    ? [to.meta.roles]
    : Array.isArray(to.meta.roles)
      ? to.meta.roles
      : [];
  const routeSpaces = spacesOfPath(to.path, opensUserSpace(routeRoles, Role.AUDITOR));

  if (currentSelectedSpace === "auditor") {
    if (!routeSpaces.includes("auditor")) {
      return navigateTo("/audit-passengers");
    }
  }
  else if (currentSelectedSpace === "city") {
    if (!routeSpaces.includes("city")) {
      return navigateTo("/city");
    }
  }
  else if (currentSelectedSpace === "user") {
    if (!routeSpaces.includes("user")) {
      return navigateTo("/");
    }
  }

  // Block city routes for users without city role
  if (!hasCityRole && isCityPath(to.path)) {
    return abortNavigation();
  }
});
